import { Application } from "pixi.js"

/**
 * The one pixi Application, held apart from the renderer that builds it.
 *
 * `PixiJsClientRenderer` owns the Application, but several low-level modules need nothing from
 * that class except `app.renderer` -- somewhere to bake a texture. Reaching for the class to get
 * it drags its entire import graph along (client_game_logic, audio, the GUI, every effect), which
 * is why `lpc_composite_generator`'s own test has to `jest.mock` the module before it can load.
 *
 * Keeping the handle here instead means the composite generator depends on a two-function module,
 * so it can be driven from anywhere that has a pixi Application -- a test, or the sprite lab --
 * without standing up the game.
 */
let rendererApp: Application
let rendererGeneration = 0

/** RenderTextures belong to their renderer context, not just the authored content. */
export const getRendererGeneration = () => rendererGeneration

export const setRendererApp = (app: Application): void => {
    if (rendererApp !== app) rendererGeneration++
    rendererApp = app
}

/** For optional bakes that have a live fallback, such as headless map tests with no renderer. */
export const findRendererApp = (): Application | undefined => rendererApp

/**
 * Throws rather than returning undefined: every caller is about to dereference `.renderer`, and
 * "cannot read renderer of undefined" three frames deep is a much worse message than this one.
 */
export const getRendererApp = (): Application => {
    if (!rendererApp) {
        throw new Error("No pixi Application registered -- call setRendererApp() before rendering")
    }
    return rendererApp
}
